/* ═══════════════════════════════════════════════════════════════
   MISSING DISCS — the pure rules behind the Missing discs section on
   the Analogue charts page (design 2026-10-06 §2, §6, §7; plan Task 1.4).

   A disc problem is the operator's record of a paper disc that was lost
   (by the driver or by the office), damaged, unreadable, missing because
   the tachograph was faulty, or never handed in by a driver who left: what
   happened, a photo of the driver's own signed note, and what the
   operator did about it.

   IT EXPLAINS A GAP AND NEVER REPLACES A DISC (design §7). The law gives
   the manual record to the driver, made at the time (Reg 165/2014 Art
   34(3)(a), 37(2)); no provision lets an operator make one afterwards. So
   nothing here makes hours, and the screen says so in plain words
   (NOTE_EXPLAINS_WORDS).

   THE RULES ARE THE SERVER'S (src/analogue_disc_problems.py, KIND_RULES):
   repeated here so a manager is told in the browser rather than by a 400
   they have to decode, and held to the Python by missingDiscs.test.mjs, so
   the two cannot drift apart without a failing test. If they ever disagree
   the server wins: it is the one that writes.

   No React, no fetch. '' / [] / null on rubbish, never a throw.
   ═══════════════════════════════════════════════════════════════ */

import { fmtDayWords } from './today.js';
import { checkFile } from './evidence.js';
import { HALF_FILED_STATUSES } from './analogueCharts.js';
import { buildNavQuery } from './nav.js';
import { confirmedBefore, isRealDate, str, typedOf, REGISTRATION_MAX } from './analogueReview.js';
import { plausibleMissing, vrnKey } from './vehicleDaySource.js';

const isObj = (x) => Boolean(x) && typeof x === 'object' && !Array.isArray(x);
const lower = (v) => (typeof v === 'string' ? v.trim().toLowerCase() : '');

/* ── the vocabulary ─────────────────────────────────────────── */

/* One row per kind, as the server's KIND_RULES has it, in its order. `words`
   is the server's KIND_WORDS: what happened, in the words a record leaves
   behind. The note: only a driver who left without handing his discs in goes
   without one, since he is not there to write it. Whose disc: losing one or
   keeping one is a person's doing, a faulty tachograph is the truck's. The
   disc itself: only a damaged or unreadable disc is one TachoParser may hold,
   and only a damaged one has a spare that replaced it, kept with it (Art
   35(1)). */
const rule = (words, needsNote, needsDriver, needsVehicle, takesChart, takesSpare, takesRepairDate) =>
  Object.freeze({ words, needsNote, needsDriver, needsVehicle, takesChart, takesSpare, takesRepairDate });

export const KIND_RULES = Object.freeze({
  lost_driver: rule('the driver lost the disc', true, true, false, false, false, false),
  lost_office: rule('the office lost the disc', true, false, false, false, false, false),
  damaged: rule('the disc was damaged', true, false, false, true, true, false),
  unreadable: rule('the disc could not be read', true, false, false, true, false, false),
  tachograph_fault: rule('the tachograph was faulty', true, false, true, false, false, true),
  not_returned: rule('the driver did not hand the disc in', false, true, false, false, false, false),
});

export const KINDS = Object.freeze(Object.keys(KIND_RULES));

/* What the operator did about it. An investigation that ends differently is
   an edit, which supersedes the record and never overwrites it. */
export const OUTCOMES = Object.freeze(['debrief', 'training', 'disciplinary', 'no_action']);

const OUTCOME_LABELS = Object.freeze({
  debrief: 'Debrief',
  training: 'Training',
  disciplinary: 'Disciplinary',
  no_action: 'No action',
});

/* A disc a leaver kept is labelled as its close reason is, "Disc not handed
   in" (Zed, 2026-10-07): he did not lose it, so it is not "lost". */
const LABEL_OVERRIDES = Object.freeze({ not_returned: 'Disc not handed in' });

/** The kind in words: the server's sentence with a capital. '' for anything else. */
export function kindLabel(kind) {
  const key = str(kind);
  if (!Object.prototype.hasOwnProperty.call(KIND_RULES, key)) return '';
  if (LABEL_OVERRIDES[key]) return LABEL_OVERRIDES[key];
  const words = KIND_RULES[key].words;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The outcome in words. '' for anything else. */
export function outcomeLabel(outcome) {
  return OUTCOME_LABELS[str(outcome)] || '';
}

/* The sentence the dialog says above the photo. A manager who reads it must
   not come away thinking the note stands in for the disc: the days it should
   have covered stay without a record, and TachoParser adds no hours. */
export const NOTE_EXPLAINS_WORDS =
  "The driver's signed note explains the gap. It does not replace the disc: the days it "
  + 'should have covered stay without a record, and no hours are added for them.';

/* ── the photo rule, and what each kind shows ───────────────── */

const ruleOf = (kind) => KIND_RULES[str(kind)] || null;

/**
 * Whether the photo of the driver's signed note has to come with this save:
 * every kind but a disc not handed in. `hasNote` is an edit of a record whose
 * note is on file and live: the server carries it over unless a new photo
 * comes, so none is asked for. False with no kind chosen yet.
 */
export function photoRequired(kind, { hasNote = false } = {}) {
  const r = ruleOf(kind);
  return Boolean(r && r.needsNote && !hasNote);
}

/** Which of the kind's own boxes the dialog shows: the disc, the spare, the repair date. */
export function kindShows(kind) {
  const r = ruleOf(kind);
  return { chart: Boolean(r && r.takesChart), spare: Boolean(r && r.takesSpare), repairDate: Boolean(r && r.takesRepairDate) };
}

/* ── the form ───────────────────────────────────────────────── */

/** The dialog's boxes, all empty: every column a record takes from a manager. */
export function blankProblem() {
  return {
    kind: '',
    driver_id: '',
    vehicle_registration: '',
    date_start: '',
    date_end: '',
    chart_id: '',
    replacement_chart_id: '',
    repair_date: '',
    outcome: '',
    outcome_note: '',
    gap_key: '',
  };
}

/* The server's bound on the outcome note (src/analogue_charts.py NOTES_MAX). */
const NOTE_MAX = 2000;

/* A plate as it is sent: upper case, one space between its parts, as the
   upload rows send one (analogueFacts.factsForUpload). */
const plateText = (v) => str(v).toUpperCase().replace(/\s+/g, ' ');

/* A typed date: '' when blank, else it as typed, for isRealDate to judge. */
const dateText = (v) => str(v);

/**
 * Every problem with the form as it stands, as [{field, words}] in the order
 * the dialog reads: the server's checks (src/analogue_disc_problems.py
 * validate, and the doors' photo rule), said before the click.
 *
 * `today` is the yard's day ('YYYY-MM-DD', ukToday): a disc cannot be
 * missing for a day that has not happened yet. `photo` is the file chosen,
 * if any; `hasNote` an edit's live note on file. `badDates` says which date
 * boxes hold a date half typed or impossible, which their value ('') cannot
 * say (the upload rows' rule, analogueFacts.js).
 *
 * Only the boxes the kind takes are judged: the others are not sent.
 */
export function problemProblems(form, { today = '', photo = null, hasNote = false, badDates = {} } = {}) {
  const f = isObj(form) ? form : {};
  const bad = isObj(badDates) ? badDates : {};
  const out = [];
  const say = (field, words) => out.push({ field, words });
  const kind = str(f.kind);
  const r = ruleOf(kind);
  if (!r) say('kind', 'Choose what happened to the disc.');

  const driver = str(f.driver_id);
  const vehicle = str(f.vehicle_registration);
  if (r && r.needsDriver && !driver) {
    say('driver_id', `Name the driver: ${r.words}, so the record has to say who.`);
  } else if (r && r.needsVehicle && !vehicle) {
    say('vehicle_registration', `Name the vehicle: ${r.words}, so the record has to say which vehicle's.`);
  } else if (!driver && !vehicle) {
    say('driver_id', 'Name the driver, the vehicle or both: the record has to say whose disc it was.');
  }
  if (vehicle.length > REGISTRATION_MAX) {
    say('vehicle_registration', `That registration is longer than ${REGISTRATION_MAX} characters.`);
  }

  const start = dateText(f.date_start);
  const end = dateText(f.date_end);
  const startOk = !bad.date_start && isRealDate(start);
  const endOk = !bad.date_end && (end === '' || isRealDate(end));
  if (bad.date_start === true || (start && !isRealDate(start))) {
    say('date_start', 'The first day is not a real date.');
  } else if (!start) {
    say('date_start', 'Enter the first day the disc should have covered.');
  }
  if (!endOk) say('date_end', 'The last day is not a real date.');
  if (startOk && endOk && end && end < start) {
    say('date_end', 'The last day cannot be before the first day.');
  }
  /* The server compares the end (the start when there is none) with today. */
  const last = endOk && end ? end : start;
  const lastOk = endOk && end ? true : startOk;
  if (lastOk && today && last > today) {
    say(end && endOk ? 'date_end' : 'date_start',
      'The dates cannot be after today: a disc cannot be missing for a day that has not happened yet.');
  }

  if (r && r.takesSpare) {
    const spare = str(f.replacement_chart_id);
    if (spare && spare === str(f.chart_id)) {
      say('replacement_chart_id', 'The spare must be another disc: a damaged disc cannot be its own spare.');
    }
  }
  if (r && r.takesRepairDate) {
    const repair = dateText(f.repair_date);
    if (bad.repair_date === true || (repair && !isRealDate(repair))) {
      say('repair_date', 'The repair date is not a real date.');
    } else if (repair && startOk && repair < start) {
      say('repair_date', 'The repair date cannot be before the first day: a tachograph is repaired after it fails.');
    }
  }

  if (photo) {
    const why = checkFile(photo);
    if (why) say('photo', why);
  } else if (photoRequired(kind, { hasNote })) {
    say('photo', "Add the photo of the driver's signed note: it is the evidence the record stands on.");
  }

  if (!OUTCOMES.includes(str(f.outcome))) say('outcome', 'Choose the outcome.');
  if (str(f.outcome_note).length > NOTE_MAX) {
    say('outcome_note', `The note is longer than ${NOTE_MAX} characters.`);
  }
  return out;
}

/**
 * The record's fields as they are sent: trimmed, the plate as a plate, and
 * only the boxes the kind takes (a disc, a spare or a repair date left in a
 * box of another kind is not sent, and the server would refuse it). A blank
 * box is left out: an edit sends the whole record, and a box left empty is a
 * box cleared.
 */
export function problemFields(form) {
  const f = isObj(form) ? form : {};
  const r = ruleOf(f.kind);
  const out = {};
  const put = (key, value) => {
    if (value) out[key] = value;
  };
  put('kind', str(f.kind));
  put('driver_id', str(f.driver_id));
  put('vehicle_registration', plateText(f.vehicle_registration));
  put('date_start', str(f.date_start));
  put('date_end', str(f.date_end));
  if (r && r.takesChart) put('chart_id', str(f.chart_id));
  if (r && r.takesSpare) put('replacement_chart_id', str(f.replacement_chart_id));
  if (r && r.takesRepairDate) put('repair_date', str(f.repair_date));
  put('outcome', str(f.outcome));
  put('outcome_note', str(f.outcome_note));
  put('gap_key', str(f.gap_key));
  return out;
}

/* ── the yard's day ─────────────────────────────────────────── */

const UK_DAY = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
});

/**
 * Today in the UK as 'YYYY-MM-DD', whatever zone the browser is in: the
 * server judges "after today" by the yard's clock (_uk_today), and a manager
 * abroad must not be told a date is fine that the server then refuses.
 */
export function ukToday(now = new Date()) {
  if (!(now instanceof Date) || Number.isNaN(now.getTime())) return '';
  const parts = UK_DAY.formatToParts(now);
  const part = (type) => (parts.find((p) => p.type === type) || {}).value || '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

/* ── the list ───────────────────────────────────────────────── */

/**
 * The current records out of whatever the list door answered: its envelope
 * or a bare array. A superseded version is left out (the door leaves them out
 * already; this is the screen's own guard). [] on rubbish.
 */
export function problemRows(payload) {
  const raw = Array.isArray(payload) ? payload : isObj(payload) && Array.isArray(payload.problems) ? payload.problems : [];
  return raw.filter((p) => isObj(p) && !str(p.superseded_at));
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
  'September', 'October', 'November', 'December'];

/** 'October 2026' for '2026-10'; '' for anything else. */
export function monthWords(ym) {
  const m = /^(\d{4})-(\d{2})$/.exec(str(ym));
  if (!m) return '';
  const month = Number(m[2]);
  return month >= 1 && month <= 12 ? `${MONTHS[month - 1]} ${m[1]}` : '';
}

/* The server's order: the newest disc first (date_start), then the latest
   recorded, then the id. */
function newestFirst(a, b) {
  return str(b.date_start).localeCompare(str(a.date_start))
    || str(b.created_at).localeCompare(str(a.created_at))
    || str(a.problem_id).localeCompare(str(b.problem_id));
}

/**
 * The records grouped by the month the disc should have covered (its first
 * day), newest month first, each in the server's order:
 * [{key: 'YYYY-MM', label: 'October 2026', problems}]. A record whose first
 * day is not a day goes last, under 'Date not known', never dropped.
 */
export function groupProblems(problems) {
  const rows = problemRows(problems).slice().sort(newestFirst);
  const groups = new Map();
  for (const p of rows) {
    const day = str(p.date_start).slice(0, 10);
    const key = isRealDate(day) ? day.slice(0, 7) : 'unknown';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(p);
  }
  const keys = [...groups.keys()].filter((k) => k !== 'unknown').sort().reverse();
  if (groups.has('unknown')) keys.push('unknown');
  return keys.map((key) => ({
    key,
    label: key === 'unknown' ? 'Date not known' : monthWords(key),
    problems: groups.get(key),
  }));
}

/** 'Mon 5 Oct', or 'Mon 5 Oct to Wed 7 Oct' over several days; '' when unknown. */
export function problemDatesWords(problem) {
  const p = isObj(problem) ? problem : {};
  const start = fmtDayWords(str(p.date_start).slice(0, 10));
  if (!start) return '';
  const end = fmtDayWords(str(p.date_end).slice(0, 10));
  return end && end !== start ? `${start} to ${end}` : start;
}

/**
 * Who and which truck: 'Ash Example · AB12 CDE', either alone, '' when
 * neither. A driver the roster has lost since is said to be one, never shown
 * as nobody: the record still names them.
 */
export function problemWhoWords(problem) {
  const p = isObj(problem) ? problem : {};
  const name = str(p.driver_name) || (str(p.driver_id) ? 'A driver no longer on your roster' : '');
  return [name, str(p.vehicle_registration)].filter(Boolean).join(' · ');
}

/**
 * The driver's note on a record: `{id, words}`, `id` the evidence file to
 * open while it is live, '' when there is nothing to open, with the words
 * that say why.
 */
export function noteOf(problem) {
  const p = isObj(problem) ? problem : {};
  const ev = isObj(p.evidence) ? p.evidence : null;
  if (ev && str(ev.id) && lower(ev.status) === 'live') return { id: str(ev.id), words: "The driver's note" };
  if (ev && str(ev.id)) return { id: '', words: "The driver's note was removed" };
  if (str(p.kind) === 'not_returned') return { id: '', words: 'No note needed: the driver left' };
  return { id: '', words: 'No note on file' };
}

/** Whether an edit of this record can carry its note over: one is on file and live. */
export function problemHasNote(problem) {
  return noteOf(problem).id !== '';
}

/* ── where a record starts ──────────────────────────────────── */

/* The states of a disc the reader could not read, the server's
   _ANALOGUE_UNREADABLE_STATES: a record of an unreadable disc rejects it from
   these, and only these. */
const UNREADABLE_STATES = Object.freeze(['rejected_quality', 'failed']);

/**
 * Whether the disc list offers "Reject, record why" on a disc: one the reader
 * could not read, and not one whose confirm did not finish (the server
 * refuses to reject that one, as its hours may be filed).
 */
export function canRecordUnreadable(chart) {
  if (!isObj(chart)) return false;
  return UNREADABLE_STATES.includes(lower(chart.review_status))
    && !HALF_FILED_STATUSES.includes(lower(chart.status));
}

/**
 * Whether saving this form rejects the disc it names, as the server does in
 * the same request: an unreadable record naming a disc that is still at one
 * of the two unreadable states. A disc already rejected is recorded against
 * and left as it is. The dialog says so before the click.
 */
export function rejectsDisc(form, charts) {
  const f = isObj(form) ? form : {};
  const id = str(f.chart_id);
  if (str(f.kind) !== 'unreadable' || !id) return false;
  const chart = (Array.isArray(charts) ? charts : []).find((c) => isObj(c) && str(c.chart_id) === id);
  /* The list button's own rule: a disc whose confirm did not finish is one
     the server refuses to reject, so saving would be refused, not reject it. */
  return canRecordUnreadable(chart);
}

/* A day as a box takes it, or '' when it is not one. */
const dayOrBlank = (v) => {
  const day = str(v).slice(0, 10);
  return isRealDate(day) ? day : '';
};

/**
 * The record a disc that could not be read starts: kind unreadable, the disc,
 * and its driver, truck and dates. `chart` is the disc list's row; `detail`
 * the disc's own read (GET /api/analogue-charts/{id}), when it has come.
 *
 * What was typed at upload wins (typed_json): for a disc the reader could not
 * read it is the only record of whose disc it was (design §2). The list row's
 * columns fill what was not typed. Once a confirm has run, those columns hold
 * the manager's own values, and they win (the check screen's rule,
 * analogueReview.confirmedBefore).
 */
export function prefillFromDisc(chart, detail = null) {
  const c = isObj(chart) ? chart : {};
  const typed = detail && !confirmedBefore(detail) ? typedOf(detail) : {};
  const typedStr = (key) => (typeof typed[key] === 'string' ? typed[key].trim() : '');
  const pick = (typedKey, column) => typedStr(typedKey) || str(c[column]);
  return {
    ...blankProblem(),
    kind: 'unreadable',
    chart_id: str(c.chart_id),
    driver_id: pick('driver_id', 'driver_id'),
    vehicle_registration: pick('vehicle_registration', 'vehicle_registration'),
    date_start: dayOrBlank(pick('chart_date_start', 'chart_date_start')),
    date_end: dayOrBlank(pick('chart_date_end', 'chart_date_end')),
  };
}

/**
 * A later pre-fill laid over the form: each box still as the first pre-fill
 * left it takes the later value, and a box the manager has changed keeps
 * theirs. The disc's typed facts arrive after the dialog opens.
 */
export function mergePrefill(current, before, after) {
  const mine = isObj(current) ? current : {};
  const was = isObj(before) ? before : {};
  const next = { ...mine };
  for (const [key, value] of Object.entries(isObj(after) ? after : {})) {
    if (mine[key] === was[key]) next[key] = value;
  }
  return next;
}

/** The dialog's boxes for an edit, from the record as it is. */
export function formFromProblem(problem) {
  if (!isObj(problem)) return blankProblem();
  const out = blankProblem();
  for (const key of Object.keys(out)) {
    const v = problem[key];
    out[key] = typeof v === 'string' ? v : '';
  }
  return out;
}

/* ── the disc pickers ───────────────────────────────────────── */

/* A disc as a picker names it: its day, plate, driver and photo. */
function discLabel(chart) {
  return [
    fmtDayWords(str(chart.chart_date_start).slice(0, 10)) || 'Date not known',
    str(chart.vehicle_registration),
    str(chart.driver_name_resolved),
    str(chart.original_filename),
  ].filter(Boolean).join(' · ');
}

/* The discs an unreadable record may name: one the list offers "Reject,
   record why" on (the two states it rejects from, never a half-filed disc,
   which the server refuses to reject), and one already rejected, which it is
   recorded against and left as it is. */
const unreadablePick = (c) => canRecordUnreadable(c) || lower(c.review_status) === 'discarded';

/**
 * The org's discs as picker options [{value, label}], newest disc first.
 * `only: 'unreadable'` keeps the discs an unreadable record may name;
 * `exclude` leaves one out (the damaged disc, from its spare's picker); `keep`
 * is the disc the record already names, which stays an option even when the
 * filter leaves it out or the list no longer has it, first.
 */
export function discChoices(charts, { only = '', exclude = '', keep = '' } = {}) {
  const all = (Array.isArray(charts) ? charts : []).filter((c) => isObj(c) && str(c.chart_id));
  const wanted = all.filter((c) => (only === 'unreadable' ? unreadablePick(c) : true)
    && str(c.chart_id) !== str(exclude));
  wanted.sort((a, b) => str(b.chart_date_start).localeCompare(str(a.chart_date_start))
    || str(b.received_at).localeCompare(str(a.received_at))
    || str(a.chart_id).localeCompare(str(b.chart_id)));
  const options = wanted.map((c) => ({ value: str(c.chart_id), label: discLabel(c) }));
  const kept = str(keep);
  if (kept && !options.some((o) => o.value === kept)) {
    const known = all.find((c) => str(c.chart_id) === kept);
    options.unshift({ value: kept, label: known ? discLabel(known) : 'A disc not in the list' });
  }
  return options;
}

/* ── after saving ───────────────────────────────────────────── */

/* The process the org follows, as the answer names it: its signed version's
   number, or 'preset' (the Earned Recognition preset) when nothing is signed. */
function processWords(version) {
  if (version === 'preset') return 'the Earned Recognition preset';
  const n = typeof version === 'number' ? version : Number(str(version));
  return Number.isInteger(n) && n > 0 ? `version ${n}` : '';
}

/**
 * What the answer's disciplinary hint becomes on the screen: an OFFER, never
 * a start (design §2). `{href, label, words}`: the driver's page in People,
 * built with lib/nav's deep link from the answer's own `nav`, for a link the
 * manager may follow or not. A record that names no driver has nobody to
 * offer it for: `href` '' and words that say so. null for any other outcome.
 */
export function disciplinaryOffer(answer) {
  const d = isObj(answer) && isObj(answer.disciplinary) ? answer.disciplinary : null;
  if (!d) return null;
  if (d.offer !== true || !isObj(d.nav)) {
    return {
      href: '',
      label: '',
      words: 'You chose disciplinary, but this record names no driver, so there is no one to open the '
        + 'disciplinary process for. Edit the record to name the driver.',
    };
  }
  const name = str(d.driver_name) || 'the driver';
  const process = processWords(d.process_version);
  return {
    href: `/?${buildNavQuery(d.nav)}`,
    label: `Open ${name}'s page in People`,
    words: `You chose disciplinary. Nothing has been started: the disciplinary process is on ${name}'s `
      + `page in People${process ? `, where your process (${process}) is followed` : ''}.`,
  };
}

/** What a save did, in a line: recorded or edited, a disc rejected, a gap closed. */
export function savedWords(answer) {
  const a = isObj(answer) ? answer : {};
  const parts = [a.superseded ? 'Saved. The earlier version is kept.' : 'Recorded.'];
  if (isObj(a.rejected_chart)) parts.push('The disc was rejected, with this record as the reason.');
  if (isObj(a.gap_closure)) parts.push('The gap it explains is closed.');
  return parts.join(' ');
}

/* ── the pickers ────────────────────────────────────────────── */

/**
 * The drivers and vehicles as a dialog opens: a load that failed is
 * forgotten, so opening the dialog again asks again (the dialog tells the
 * manager to close it and try again); one that worked is kept.
 */
export function pickersOnOpen(pickers) {
  return isObj(pickers) && pickers.failed ? null : pickers;
}

/* ═══════════════════════════════════════════════════════════════
   THE FIRST LIST: POSSIBLE MISSING DISCS (design §1, §6; plan Task 1.4)

   GET /api/analogue-discs/missing answers {gaps, held, problems, counts}
   (api/server.py get_analogue_discs_missing): the odometer gaps between a
   truck's filed discs, the possible gaps still waiting on something, and
   the readings a manager has to put right. The server decides what each
   one IS; these say it, count it and act on it.
   ═══════════════════════════════════════════════════════════════ */

/* The sidebar's count. The section says it whenever its list loads, so the
   Analogue charts entry moves with what the manager has just done; the
   sidebar asks for the list itself when it first shows the entry. A window
   event, so the two need no shared store (the page and the sidebar are
   siblings under the app). */
export const MISSING_DISCS_COUNT_EVENT = 'tp:missing-discs-count';

/** Tell the sidebar the section's count (missingLists' `badge`). */
export function announceMissingCount(count) {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
  const n = typeof count === 'number' && Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
  try {
    window.dispatchEvent(new CustomEvent(MISSING_DISCS_COUNT_EVENT, { detail: n }));
  } catch {
    /* A browser without CustomEvent: the sidebar keeps the count it asked for. */
  }
}

/* The chain's tiers and problems, as src/analogue_chain.py names them. */
const TIER_YARD = 'yard';
const HELD_CHECK_READING = 'check_reading';
/* The problems a manager can clear (MANAGER_PROBLEMS). A vehicle-change
   reading TP did not capture is TP's record falling short, not his. */
const CHAIN_PROBLEMS = Object.freeze(['backwards', 'odometer_missing']);
const CHAIN_PROBLEM_WORDS = Object.freeze({
  backwards: 'Check the reading',
  odometer_missing: 'Odometer missing on this disc',
});

const objects = (v) => (Array.isArray(v) ? v.filter(isObj) : []);
const finite = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const KM = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 1 });
/* '10,120', or '' when it is not a number. */
const kmText = (v) => {
  const n = finite(typeof v === 'string' && v.trim() !== '' ? Number(v) : v);
  return n === null ? '' : KM.format(n);
};
const dayOf = (v) => fmtDayWords(str(v).slice(0, 10));

/**
 * The list as the section shows it: `gaps` (5 km or more), `yard` (under
 * 5 km, "likely yard", shown quietly with a quick close), `held` and
 * `problems`, each in the server's order (newest first). `count` is every
 * row; `badge`, the sidebar's count, leaves out the likely-yard gaps, which
 * the server counts nowhere (unaccounted_mileage.is_yard_shuffle).
 * `missingKm` is the server's own open km. Empty, never a throw, on rubbish.
 *
 * An ASSIGNED gap stays in `gaps` (Zed 2026-10-10, "Stay red"): who drove it
 * is not why the disc is missing.
 */
export function missingLists(payload) {
  const p = isObj(payload) ? payload : {};
  const all = objects(p.gaps);
  const gaps = all.filter((g) => str(g.tier) !== TIER_YARD);
  const yard = all.filter((g) => str(g.tier) === TIER_YARD);
  const held = objects(p.held);
  const problems = objects(p.problems).filter((x) => CHAIN_PROBLEMS.includes(str(x.kind)));
  const counted = isObj(p.counts) ? finite(p.counts.missing_km) : null;
  const missingKm = counted !== null ? counted
    : gaps.filter((g) => str(g.status) !== 'assigned').reduce((sum, g) => sum + (finite(g.km) || 0), 0);
  return {
    gaps,
    yard,
    held,
    problems,
    count: gaps.length + yard.length + held.length + problems.length,
    badge: gaps.length + held.length + problems.length,
    missingKm,
  };
}

/* "the disc ending Mon 5 Oct (Ash Example, 10,000 km)". */
function discSide(which, s) {
  const d = isObj(s) ? s : {};
  const day = dayOf(which === 'ending' ? d.date_end : d.date_start) || 'a day not known';
  const reading = kmText(which === 'ending' ? d.odometer_end : d.odometer_start);
  const extra = [str(d.driver), reading && `${reading} km`].filter(Boolean).join(', ');
  return `the disc ${which} ${day}${extra ? ` (${extra})` : ''}`;
}

/**
 * A gap in words: `title` ('120 km with no disc · AB12CDE'), `between` (the
 * discs either side, their days, drivers and readings), `yard` (the quiet
 * "likely yard" line, or '') and `assigned` (why an assigned gap is still
 * here, or ''). All '' on rubbish.
 */
export function gapWords(gap) {
  if (!isObj(gap)) return { title: '', between: '', yard: '', assigned: '' };
  const km = kmText(gap.km);
  const vrn = str(gap.vrn);
  return {
    title: [km ? `${km} km with no disc` : 'A gap with no disc', vrn].filter(Boolean).join(' · '),
    between: `Between ${discSide('ending', gap.prev)} and ${discSide('starting', gap.next)}`,
    yard: str(gap.tier) === TIER_YARD ? 'Likely yard: under 5 km.' : '',
    assigned: str(gap.status) === 'assigned'
      ? 'Assigned to a driver. It stays here until you record what happened or close it.'
      : '',
  };
}

/* A disc as a held gap names it: its day and driver, out of the page's list. */
function discName(chartId, charts) {
  const chart = (Array.isArray(charts) ? charts : []).find((c) => isObj(c) && str(c.chart_id) === str(chartId));
  if (!chart) return 'a disc not in the list';
  const day = dayOf(chart.chart_date_start) || 'a day not known';
  const driver = str(chart.driver_name_resolved);
  return `the disc of ${day}${driver ? ` (${driver})` : ''}`;
}

const andList = (items) => (items.length <= 1 ? items.join('')
  : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`);

/**
 * A held gap in words: "possible gap, waiting on disc X", or "check the
 * reading first" after a reading that went backwards. '' on rubbish.
 * `charts` is the page's disc list, to name the discs it waits on.
 */
export function heldWords(held, charts) {
  if (!isObj(held)) return '';
  const km = kmText(held.km);
  const vrn = str(held.vrn);
  const head = `Possible gap${km ? ` of ${km} km` : ''}${vrn ? ` on ${vrn}` : ''}`;
  if (str(held.waiting_on) === HELD_CHECK_READING) {
    return `${head}: check the reading first. A reading on the disc before it went backwards.`;
  }
  const ids = Array.isArray(held.held_by) ? held.held_by.filter((id) => str(id)) : [];
  if (!ids.length) return `${head}, waiting on a disc nobody has confirmed yet.`;
  return `${head}, waiting on ${andList(ids.map((id) => discName(id, charts)))}. `
    + `Confirm ${ids.length > 1 ? 'them' : 'it'} and the gap is measured again.`;
}

/* Which readings an odometer_missing problem names as blank. */
function blankReadings(missing) {
  const said = Array.isArray(missing) ? missing.map(str) : [str(missing)];
  const start = said.includes('start');
  const end = said.includes('end');
  if (start && end) return 'the starting and finishing readings are blank';
  if (start) return 'the starting reading is blank';
  if (end) return 'the finishing reading is blank';
  return 'the readings are blank';
}

/**
 * A reading to put right, in words: `title` (the server's words and the
 * truck), `detail` (which reading, on which disc) and `charts`, the discs
 * the manager may open with Correct. Empty on rubbish.
 */
export function chainProblemWords(problem) {
  if (!isObj(problem)) return { title: '', detail: '', charts: [] };
  const kind = str(problem.kind);
  const vrn = str(problem.vrn);
  const title = [str(problem.words) || CHAIN_PROBLEM_WORDS[kind] || 'Check this disc', vrn]
    .filter(Boolean).join(' · ');
  if (kind === 'backwards') {
    const prev = isObj(problem.prev) ? problem.prev : {};
    const next = isObj(problem.next) ? problem.next : {};
    return {
      title,
      detail: `The disc starting ${dayOf(next.date_start) || 'on a day not known'} reads `
        + `${kmText(next.odometer_start) || 'a reading'} km, below the `
        + `${kmText(prev.odometer_end) || 'higher'} km the disc ending `
        + `${dayOf(prev.date_end) || 'before it'} ended on. One of the two readings is wrong.`,
      charts: [str(prev.chart_id), str(next.chart_id)].filter(Boolean),
    };
  }
  if (kind === 'odometer_missing') {
    const start = dayOf(problem.date_start);
    const end = dayOf(problem.date_end);
    const days = start && end && end !== start ? `${start} to ${end}` : start || 'A day not known';
    const driver = str(problem.driver);
    return {
      title,
      detail: `${days}${driver ? ` (${driver})` : ''}: ${blankReadings(problem.missing)}.`,
      charts: [str(problem.chart_id)].filter(Boolean),
    };
  }
  return { title, detail: '', charts: [] };
}

/* 'YYYY-MM-DD' moved by `n` days, or '' when it is not a day. */
function addDays(iso, n) {
  const day = str(iso).slice(0, 10);
  if (!isRealDate(day)) return '';
  const [y, m, d] = day.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return t.toISOString().slice(0, 10);
}

/**
 * The record a gap starts: its truck, the gap it explains (gap_key, so
 * saving closes it), and the days the missing disc should have covered: the
 * WHOLE days between the disc before and the disc after, the server's own
 * reading of a gap (_analogue_missing_disc_days). With no whole day between
 * them, the day the truck reappears, which is where the server hangs it.
 * The driver only when the same driver is on both discs: otherwise nobody
 * knows whose disc it was, and the manager names him. The kind is his to
 * choose.
 */
export function prefillFromGap(gap) {
  if (!isObj(gap)) return blankProblem();
  const prev = isObj(gap.prev) ? gap.prev : {};
  const next = isObj(gap.next) ? gap.next : {};
  const first = addDays(prev.date_end, 1);
  const last = addDays(next.date_start, -1);
  let start = '';
  let end = '';
  if (first && last && first <= last) {
    start = first;
    end = last === first ? '' : last;
  } else {
    start = dayOrBlank(next.date_start);
  }
  const prevDriver = str(prev.driver_id);
  return {
    ...blankProblem(),
    gap_key: str(gap.gap_key),
    vehicle_registration: str(gap.vrn),
    date_start: start,
    date_end: end,
    driver_id: prevDriver && prevDriver === str(next.driver_id) ? prevDriver : '',
  };
}

/* The reasons the section's Close offers. A disc that was lost, damaged,
   unreadable or not handed in, or a faulty tachograph, is NOT closed here:
   it is recorded with "Record what happened", with the driver's signed note,
   and that record closes the gap with its kind's reason (the server's
   _disc_problem_save). Offering those reasons on a bare close would close a
   gap with no note behind it (closureConstants.js keeps them display only
   for the same reason). What is left needs no note from the driver: the
   yard, out of scope, or something else the manager writes down. */
export const GAP_CLOSE_REASONS = Object.freeze(['yard_shunting', 'out_of_scope', 'other']);

/* The close dialogs' rule (MissingMileageTab.jsx): a note of ten characters. */
export const CLOSE_NOTE_MIN = 10;

/** What stops a close, as [{field, words}]: a reason the section offers, and the note. */
export function closeGapProblems({ reason = '', note = '' } = {}) {
  const out = [];
  if (!GAP_CLOSE_REASONS.includes(str(reason))) {
    out.push({ field: 'reason', words: 'Choose a reason. A lost, damaged or unreadable disc is recorded with Record what happened.' });
  }
  if (str(note).length < CLOSE_NOTE_MIN) {
    out.push({ field: 'note', words: `Write a note of at least ${CLOSE_NOTE_MIN} characters: what happened, for the record.` });
  }
  return out;
}

/**
 * The close door's body for one gap (POST /api/mileage/close). It ALWAYS
 * carries the gap's own gap_key and row id: a chain gap is closed by its key
 * alone (the server's _closure_target), so the closure marks that gap and
 * never a digital gap on the same truck-day. The day and truck ride along
 * for the record; the server takes them off the gap.
 */
export function closeGapBody(gap, { reason = '', note = '' } = {}) {
  const g = isObj(gap) ? gap : {};
  return {
    gap_key: str(g.gap_key),
    id: g.id,
    date: str(g.date).slice(0, 10),
    vehicle_registration: str(g.vrn),
    issue_type: 'odometer_gap',
    reason_code: str(reason),
    comment: str(note),
  };
}

/* ═══════════════════════════════════════════════════════════════
   THE CALENDAR'S WORDS (design §3, "honest days")

   A day a disc is missing for is never "No data available", and never a
   quiet rest day: it says "Disc missing" (red) until what happened is
   recorded, then "Disc missing, explained" (grey). The server decides which
   days (_analogue_missing_disc_days); this says them.

   A PARTIAL day HAS its disc. The flag only says that km between two discs
   with no whole day between them are unaccounted, so the day keeps its
   reading and gets, at most, a quiet note. Never "Disc missing".

   WHICH TRUCK. The flag is a DAY's fact; a truck's view must only show it
   for that truck. `whole_vrns` are the trucks the day is a whole missing
   day for, `vrns` every truck marking it; a truck in neither is never
   flagged, and a truck only in `vrns` has its disc that day (partial).
   ═══════════════════════════════════════════════════════════════ */

/**
 * The words for a calendar day's `analogueMissing`: `{state ('missing' |
 * 'explained' | 'partial'), label, title, color, plates}`, or null when the
 * day has nothing to say. `vehicle` is the truck the view is about, when it
 * is one truck's. Colours are tokens: the danger red, the muted grey.
 */
export function discMissingDay(raw, { vehicle = '' } = {}) {
  const m = plausibleMissing(raw);
  if (!m) return null;
  /* Which whole trucks are still red (review I1): the server's
     unexplained_whole_vrns. An older server sends only the day-level
     `explained`, which then speaks for every whole truck. */
  const said = Array.isArray(m.unexplained_whole_vrns);
  const openKeys = new Set((said ? m.unexplained_whole_vrns : (m.explained ? [] : m.whole_vrns)).map(vrnKey));
  let partial = m.partial;
  let open = said ? openKeys.size > 0 || (!m.explained && !m.whole_vrns.length) : !m.explained;
  let plates = m.whole_vrns.filter((v) => openKeys.has(vrnKey(v)));
  let explainedPlates = m.whole_vrns.filter((v) => !openKeys.has(vrnKey(v)));
  /* A partial truck is "open" when its day is: the server keeps no
     per-truck flag for a partial mark, and the note is quiet either way. */
  let partialOpen = !m.explained || m.whole_vrns.length > 0;
  const key = vrnKey(vehicle);
  if (key) {
    const whole = m.whole_vrns.find((v) => vrnKey(v) === key);
    if (whole) {
      partial = false;
      open = openKeys.has(key);
      plates = [whole];
      explainedPlates = [];
    } else if (m.vrns.some((v) => vrnKey(v) === key)) {
      partial = true;
      /* THIS truck's own partial mark decides its note: another truck's
         red whole day, or another partial mark, says nothing about it. An
         older server keeps no per-truck flag, and the day's word stands. */
      if (Array.isArray(m.unexplained_partial_vrns)) {
        partialOpen = m.unexplained_partial_vrns.some((v) => vrnKey(v) === key);
      }
    } else {
      return null;
    }
  }
  if (!partial && !open) {
    plates = key ? plates : m.whole_vrns;
    explainedPlates = [];
  }
  const km = kmText(m.gap_km);
  if (partial) {
    if (!partialOpen) return null;
    return {
      state: 'partial',
      label: 'Km between discs unaccounted',
      color: 'var(--muted)',
      plates: [],
      title: `This day has its disc. ${km ? `${km} km` : 'Kilometres'} between it and the disc `
        + 'before are not accounted for: see Missing discs on the Analogue charts page.',
    };
  }
  const on = `No disc covers this day${plates.length ? ` on ${plates.join(', ')}` : ''}.`;
  if (!open) {
    const kind = kindLabel(m.problem_kind);
    return {
      state: 'explained',
      label: 'Disc missing, explained',
      color: 'var(--muted)',
      plates,
      explainedPlates: [],
      title: `${on} ${kind ? `${kind}, and that is recorded.` : 'What happened is recorded.'} `
        + 'The record explains the gap; it does not replace the disc.',
    };
  }
  /* Red names only the trucks still unexplained; the others on the day are
     said, in grey, as explained. */
  const others = explainedPlates.length
    ? ` What happened is recorded for ${explainedPlates.join(', ')}.`
    : '';
  return {
    state: 'missing',
    label: 'Disc missing',
    color: 'var(--danger)',
    plates,
    explainedPlates,
    title: `${on}${km ? ` ${km} km were driven with no disc to show for them.` : ''} `
      + `Record what happened in Missing discs, on the Analogue charts page.${others}`,
  };
}
